import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { type Socket } from 'socket.io-client';
import { createAppSocket } from '@/lib/socket';
import type {
  CallType,
  CallEndReason,
  CallBillingTick,
  CallRunwayPayload,
  IceServerConfig,
  CallIncomingPayload,
  PromoEndingPayload,
  ResumableCallSession,
  CallRejoinedPayload,
  CallRenegotiatePayload,
  CallMicStatus,
  CallMicStatusPayload,
  CallPauseReason,
  CallPausedPayload,
  CallerType,
} from '@klaro7/shared-types';

import { tokenManager } from '@/lib/api';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { getActiveCall, uploadRecording, uploadRecordingChunk } from '../services/call.service';
import type { AnswerCallInfo } from '../services/answer.service';
import { playPromoAlert } from '../lib/ringtone';
import { startCallerTone, stopCallerTone, shouldPlayCallerTone } from '../lib/callerTone';
import { runCallPreflight } from '../lib/callPreflight';
import { detachRemoteAudio, setRemoteAudioMuted } from '../lib/remoteAudio';
import { startCallRecording, type ActiveCallRecording } from '../lib/callRecorder';
import { watchLocalMic, type MicWatchdog } from '../lib/micWatchdog';
import { keepScreenAwake, releaseScreenAwake } from '../lib/screenWakeLock';
import { reportClientErrorOnce } from '@/lib/clientErrorLog';
import { detectDeviceModel } from '@/lib/deviceModel';
import { collectNetDiagnostics, measureSocketPing } from '../lib/netDiagnostics';

const AUDIO_BLOCKED_MESSAGE =
  "Il browser sta bloccando l'audio, la chiamata non può partire. " +
  'Riprova; se il problema continua, apri il sito direttamente in Chrome o ' +
  "Safari invece che nel browser integrato di un'altra app (es. Messaggi, " +
  'Instagram, Facebook).';

export type CallState =
  | 'idle'
  | 'initiating'
  | 'ringing'
  | 'incoming'
  | 'connecting'
  | 'active'
  | 'paused'
  | 'ending';

export interface PauseInfo {
  graceDeadline: string;
  reason: CallPauseReason;
}

interface CallInfo {
  callSessionId: string;
  callType: CallType;
  remoteName: string;
  remoteAvatar?: string;
  // ISO date string — the caller's DOB, populated only on the professional
  // (callee) side so it can be shown under the caller's name during the call.
  remoteDateOfBirth?: string;
  costPerMinute: number;
  professionalId?: string;
  callerUserId?: string;
  // Tipologia commerciale del chiamante, popolata SOLO lato professionista
  // (callee) per mostrare il badge accanto al nome. Mai un importo in €.
  callerType?: CallerType;
  // Paid CHAT: the conversation pre-created at initiate, threaded to both sides
  // so the professional can open the thread and write first.
  conversationId?: string;
}

interface CallEndInfo {
  durationSeconds: number;
  totalCost: number;
  endReason: CallEndReason;
  callSessionId: string;
  /** The professional's own net compenso for this call — only ever set on the
   * payload the pro receives, never the caller's. null if nothing was earned. */
  proEarning?: number | null;
}

interface CallContextValue {
  callState: CallState;
  callInfo: CallInfo | null;
  activeCallType: CallType | null;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  isAudioMuted: boolean;
  /** Cattura mic sospesa dal sistema (schermo spento/battery saver), non mute manuale. */
  micSuspended: boolean;
  /** Stato mic dell'altro lato ('muted'/'suspended'), null se il suo audio è ok. */
  remoteMicStatus: Exclude<CallMicStatus, 'ok'> | null;
  isVideoEnabled: boolean;
  elapsed: number;
  billingTick: CallBillingTick | null;
  clientRunway: CallRunwayPayload | null;
  promoEnding: PromoEndingPayload | null;
  callEndInfo: CallEndInfo | null;
  incomingCall: CallIncomingPayload | null;
  callError: string | null;
  /** 'info' per esiti non tecnici (nessuna risposta, rifiutata) — il toast non deve leggersi come guasto. */
  callErrorSeverity: 'error' | 'info';
  phoneVerificationRequired: boolean;
  pauseInfo: PauseInfo | null;
  pauseSession: () => void;
  resumeCall: () => void;
  resumableSession: ResumableCallSession | null;
  peerDisconnected: boolean;
  rejoinSession: () => void;
  declineResume: () => void;
  socket: Socket | null;

  // Actions
  initiateCall: (professionalId: string, callType: CallType, professionalName: string) => void;
  acceptIncomingCall: () => void;
  rejectIncomingCall: () => void;
  presentIncomingFromToken: (info: AnswerCallInfo) => void;
  cancelCall: () => void;
  endCall: () => void;
  toggleAudio: () => void;
  toggleVideo: () => void;
  dismissCallEnd: () => void;
  dismissError: () => void;
  dismissPhoneVerification: () => void;
}

const CallContext = createContext<CallContextValue | null>(null);

export function useCall() {
  const ctx = useContext(CallContext);
  if (!ctx) throw new Error('useCall must be used within CallProvider');
  return ctx;
}

export function useSharedSocket(): Socket | null {
  const ctx = useContext(CallContext);
  return ctx?.socket ?? null;
}

export function CallProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const socketRef = useRef<Socket | null>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const elapsedIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const activeRecordingRef = useRef<ActiveCallRecording | null>(null);
  const micWatchdogRef = useRef<MicWatchdog | null>(null);

  // Refs to avoid stale closures in socket event handlers
  const callTypeRef = useRef<CallType>('audio');
  // Current call identity/state, mirrored into refs so the (re)connect handler —
  // registered once inside a stable effect — can read the live value without a
  // stale closure. Used to reconcile after a dropped socket (see 'call:sync').
  const callStateRef = useRef<CallState>('idle');
  const callSessionIdRef = useRef<string | null>(null);
  const incomingSessionIdRef = useRef<string | null>(null);
  // Mirror del mute manuale per il watchdog mic: un unmute OS non deve
  // segnalare 'ok' al peer se l'utente è comunque mutato dal bottone.
  const isAudioMutedRef = useRef(false);
  // Mirror di pauseInfo per gli handler socket registrati una sola volta
  // (closure altrimenti stale): serve a sapere, al call:resumed, se la pausa
  // che si sta chiudendo era 'manual' (da ripristinare mic/audio remoto).
  const pauseInfoRef = useRef<PauseInfo | null>(null);

  // WebRTC setup synchronization: signal handlers wait for this before processing
  const pcReadyResolverRef = useRef<(() => void) | null>(null);
  const pcReadyRef = useRef<Promise<void>>(Promise.resolve());
  const pendingIceCandidatesRef = useRef<RTCIceCandidateInit[]>([]);

  const [callState, setCallState] = useState<CallState>('idle');
  const [callInfo, setCallInfo] = useState<CallInfo | null>(null);

  // Keep the ringtone AudioContext unlocked so an incoming call is audible.
  // The browser autoplay policy keeps a fresh context suspended until a gesture,
  // and mobile (iOS) re-suspends it after idle/backgrounding. So we re-prime on
  // EVERY gesture (not just the first) and whenever the page returns to the
  // foreground — maximizing the chance the context is running when a call rings.
  useEffect(() => {
    const prime = () => {
      void import('../lib/ringtone').then((m) => m.primeRingtone());
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') prime();
    };
    window.addEventListener('pointerdown', prime);
    window.addEventListener('keydown', prime);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('pointerdown', prime);
      window.removeEventListener('keydown', prime);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);
  const [activeCallType, setActiveCallType] = useState<CallType | null>(null);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [isAudioMuted, setIsAudioMuted] = useState(false);
  const [micSuspended, setMicSuspended] = useState(false);
  const [remoteMicStatus, setRemoteMicStatus] = useState<Exclude<CallMicStatus, 'ok'> | null>(null);
  const [isVideoEnabled, setIsVideoEnabled] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [billingTick, setBillingTick] = useState<CallBillingTick | null>(null);
  const [clientRunway, setClientRunway] = useState<CallRunwayPayload | null>(null);
  const [promoEnding, setPromoEnding] = useState<PromoEndingPayload | null>(null);
  const [callEndInfo, setCallEndInfo] = useState<CallEndInfo | null>(null);
  const [incomingCall, setIncomingCall] = useState<CallIncomingPayload | null>(null);
  const [callError, setCallErrorRaw] = useState<string | null>(null);
  const [callErrorSeverity, setCallErrorSeverity] = useState<'error' | 'info'>('error');
  const setCallError = useCallback((message: string | null, severity: 'error' | 'info' = 'error') => {
    setCallErrorSeverity(severity);
    setCallErrorRaw(message);
  }, []);
  const [phoneVerificationRequired, setPhoneVerificationRequired] = useState(false);
  const [pauseInfo, setPauseInfo] = useState<PauseInfo | null>(null);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [resumableSession, setResumableSession] = useState<ResumableCallSession | null>(null);
  const [peerDisconnected, setPeerDisconnected] = useState(false);

  // Connect socket when authenticated
  useEffect(() => {
    if (!isAuthenticated) return;

    const token = tokenManager.getAccessToken();
    if (!token) return;

    const socket = createAppSocket(token);

    socketRef.current = socket;
    setSocket(socket);

    // ---- Presence heartbeat ----
    // Tells the server the page is open/foregrounded so it can distinguish
    // "reachable in-app right now" from merely "online" (persisted flag). When
    // the tab is hidden or the phone screen turns off the timer is suspended and
    // the pings stop → the server marks the user unreachable and escalates an
    // incoming call to a real phone call (Twilio Voice).
    const sendPresence = () => {
      if (document.visibilityState === 'visible') socket.emit('presence:heartbeat');
    };
    // On every (re)connect: refresh presence AND reconcile call state. A socket
    // dropped mid-call (screen-lock, backgrounded tab, network blip) may have
    // missed the one-shot 'call:ended'/'cancelled'/'missed'/'rejected' broadcast,
    // leaving this side stuck on the call screen. Ask the server whether the call
    // we still believe we're in is actually live; it replays the terminal event
    // if it already ended while we were away.
    // An incoming-call modal left open while the page was backgrounded may
    // belong to a call that has since been answered elsewhere or ended: ask the
    // server, which answers 'call:incoming-expired' if it's no longer ringing.
    const syncIncoming = () => {
      const incomingId = incomingSessionIdRef.current;
      if (incomingId && callStateRef.current === 'incoming') {
        socket.emit('call:sync', { callSessionId: incomingId, incomingOnly: true });
      }
    };
    const onConnect = () => {
      sendPresence();
      const sessionId = callSessionIdRef.current;
      const state = callStateRef.current;
      if (sessionId && state !== 'idle') {
        socket.emit('call:sync', { callSessionId: sessionId });
      }
      syncIncoming();
    };
    socket.on('connect', onConnect);
    const presenceInterval = setInterval(sendPresence, 25_000);
    const onVisibilityChange = () => {
      sendPresence();
      if (document.visibilityState === 'visible' && socket.connected) syncIncoming();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    // ---- Socket event handlers ----

    socket.on('call:ringing', (data: { callSessionId: string; professionalId: string; conversationId?: string }) => {
      setCallState('ringing');
      setCallInfo((prev) =>
        prev
          ? { ...prev, callSessionId: data.callSessionId, conversationId: data.conversationId ?? prev.conversationId }
          : prev
      );
    });

    socket.on('call:incoming', (data: CallIncomingPayload) => {
      callTypeRef.current = data.callType;
      setActiveCallType(data.callType);
      setIncomingCall(data);
      setCallState('incoming');
    });

    socket.on('call:accepted', async (data: { callSessionId: string; iceServers: IceServerConfig[]; role?: 'caller' | 'callee' }) => {
      const isCaller = data.role === 'caller';
      console.log('[CALL] call:accepted received, role:', data.role, 'isCaller:', isCaller, 'callType:', callTypeRef.current);

      // Chat sessions have no audio/video media: skip all WebRTC setup and go
      // straight to the active billing state. The server already started its
      // billing tick and heartbeat monitoring on accept, so we must keep the
      // client heartbeat running for chat too.
      if (callTypeRef.current === 'chat') {
        setActiveCallType('chat');
        setCallState('active');
        startHeartbeat(data.callSessionId, socket);
        return;
      }

      // Create a promise that signal handlers will wait on
      pcReadyRef.current = new Promise((resolve) => {
        pcReadyResolverRef.current = resolve;
      });

      setCallState('connecting');
      try {
        await setupWebRTC(data.iceServers, data.callSessionId, socket, isCaller);
      } catch (err) {
        console.error('[CALL] setupWebRTC error:', err);
        setCallError('Impossibile stabilire la connessione');
        cleanup();
      }
    });

    socket.on('call:rejected', () => {
      setCallError('Il consulente ha rifiutato la chiamata', 'info');
      cleanup();
    });

    socket.on('call:incoming-expired', (data: { callSessionId: string }) => {
      if (incomingSessionIdRef.current !== data.callSessionId) return;
      if (callStateRef.current !== 'incoming') return;
      setIncomingCall(null);
      cleanup();
    });

    socket.on('call:cancelled', () => {
      setIncomingCall(null);
      cleanup();
    });

    socket.on('call:missed', () => {
      if (callState === 'ringing' || callState === 'initiating') {
        setCallError('Il consulente non ha risposto', 'info');
      }
      setIncomingCall(null);
      cleanup();
    });

    socket.on('call:ended', (data: { callSessionId: string; durationSeconds: number; totalCost: number; endReason: CallEndReason; proEarning?: number | null }) => {
      // La sessione proposta nel modal potrebbe morire mentre il modal è aperto.
      setResumableSession(null);
      // Stop recording and upload before cleanup
      stopRecording(data.callSessionId);
      setCallEndInfo({
        callSessionId: data.callSessionId,
        durationSeconds: data.durationSeconds,
        totalCost: data.totalCost,
        endReason: data.endReason,
        proEarning: data.proEarning,
      });
      cleanup();
    });

    socket.on('call:error', (data: { code: string; message: string }) => {
      // Phone-not-verified is gated by a dedicated verification modal instead of
      // surfacing the raw error toast, so the user can verify and retry.
      if (data.code === 'PHONE_NOT_VERIFIED') {
        setPhoneVerificationRequired(true);
      } else {
        setCallError(data.message);
      }
      cleanup();
    });

    // Rientro post-refresh (lato che ha perso la pagina). Se la sessione era
    // in pausa il server la ripropone con status 'paused' + motivo/scadenza:
    // va ricostruito l'overlay di pausa PRIMA del branch chat/call, altrimenti
    // il ramo chat (che esce subito, non avendo media) lo salterebbe.
    socket.on('call:rejoined', async (data: CallRejoinedPayload) => {
      if (data.status === 'paused' && data.pauseDeadline) {
        const info: PauseInfo = {
          graceDeadline: data.pauseDeadline,
          reason: data.pauseReason ?? 'insufficient_credits',
        };
        // Assegnazione sincrona: onconnectionstatechange (evento WebRTC, non
        // un render React) deve vederla già pronta quando la media si
        // riconnette, non aspettare il commit di setPauseInfo.
        pauseInfoRef.current = info;
        setPauseInfo(info);
      }

      // Chat: nessun media da ricostruire. Lo stato attivo è già impostato da
      // rejoinSession; qui va solo eventualmente corretto a 'paused'.
      if (data.callType === 'chat') {
        if (data.status === 'paused') setCallState('paused');
        return;
      }

      callTypeRef.current = data.callType;
      setActiveCallType(data.callType);
      pcReadyRef.current = new Promise((resolve) => {
        pcReadyResolverRef.current = resolve;
      });
      setCallState('connecting');
      try {
        await setupWebRTC(data.iceServers, data.callSessionId, socket, data.role === 'caller');
      } catch (err) {
        console.error('[CALL] rejoin setupWebRTC error:', err);
        setCallError('Impossibile ristabilire la connessione');
        cleanup();
      }
    });

    // L'altro lato è rientrato dopo un refresh: la sua PeerConnection è nuova,
    // la nostra va ricostruita da zero (stesso code path del connect iniziale).
    socket.on('call:renegotiate', async (data: CallRenegotiatePayload) => {
      if (callTypeRef.current === 'chat') return;
      stopRecording(data.callSessionId);
      teardownPeerConnection();
      pcReadyRef.current = new Promise((resolve) => {
        pcReadyResolverRef.current = resolve;
      });
      setCallState('connecting');
      try {
        await setupWebRTC(data.iceServers, data.callSessionId, socket, data.role === 'caller');
      } catch (err) {
        console.error('[CALL] renegotiate setupWebRTC error:', err);
        setCallError('Impossibile ristabilire la connessione');
        socket.emit('call:end', { callSessionId: data.callSessionId });
        cleanup();
      }
    });

    socket.on('call:peer-disconnected', () => {
      setPeerDisconnected(true);
    });

    // L'altro lato segnala il suo microfono muto (manuale) o sospeso dal suo
    // sistema: senza banner chi ascolta sente solo silenzio e non sa perché.
    socket.on('call:mic-status', (data: CallMicStatusPayload) => {
      setRemoteMicStatus(data.status === 'ok' ? null : data.status);
    });

    socket.on('call:peer-rejoined', () => {
      setPeerDisconnected(false);
    });

    socket.on('call:billing-tick', (data: CallBillingTick) => {
      setBillingTick(data);
    });

    // Countdown credito cliente, solo lato professionista (minuti, mai €)
    socket.on('call:runway', (data: CallRunwayPayload) => {
      setClientRunway(data);
    });

    socket.on('call:credits-warning', () => {
      // Could show a visual warning - billingTick.remainingMinutes already reflects this
    });

    socket.on('call:promo-ending', (data: PromoEndingPayload) => {
      setPromoEnding(data);
      playPromoAlert();
    });

    // Nessun 'call:credits-exhausted': all'esaurimento del credito il server
    // mette la chiamata in PAUSA ed emette 'call:paused' con reason
    // 'insufficient_credits' (finestra di grazia per ricaricare, poi
    // 'call:ended'). L'handler che c'era qui mostrava un errore in
    // contraddizione con quella UI, per un evento che nessuno emetteva.

    socket.on('call:paused', (data: CallPausedPayload) => {
      setPauseInfo({ graceDeadline: data.graceDeadline, reason: data.reason });
      setCallState('paused');
      // Pausa manuale (solo call, non chat): silenzio nei due sensi finché
      // dura, senza toccare lo stato di mute manuale sottostante — va
      // ripristinato tal quale al resume, non forzato ad "acceso".
      if (data.reason === 'manual' && callTypeRef.current !== 'chat') {
        localStreamRef.current?.getAudioTracks().forEach((t) => { t.enabled = false; });
        setRemoteAudioMuted(true);
      }
    });

    socket.on('call:resumed', () => {
      const wasManualPause = pauseInfoRef.current?.reason === 'manual';
      setPauseInfo(null);
      setCallState('active');
      if (wasManualPause && callTypeRef.current !== 'chat') {
        localStreamRef.current?.getAudioTracks().forEach((t) => { t.enabled = !isAudioMutedRef.current; });
        setRemoteAudioMuted(false);
      }
    });

    socket.on('call:resume-failed', () => {
      setCallError('Ricarica non rilevata. Riprova.');
    });

    // WebRTC signaling — handlers wait for pcReady to avoid race conditions
    socket.on('signal:offer', async (data: { callSessionId: string; sdp: RTCSessionDescriptionInit }) => {
      if (callTypeRef.current === 'chat') return;
      console.log('[CALL] Received offer, waiting for PC ready...');
      await pcReadyRef.current;
      const pc = pcRef.current;
      if (!pc) return;
      console.log('[CALL] Processing offer');
      await pc.setRemoteDescription(new RTCSessionDescription(data.sdp));

      // Drain any ICE candidates that arrived before remote description was set
      for (const c of pendingIceCandidatesRef.current) {
        await pc.addIceCandidate(new RTCIceCandidate(c));
      }
      pendingIceCandidatesRef.current = [];

      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      socket.emit('signal:answer', { callSessionId: data.callSessionId, sdp: answer });
      console.log('[CALL] Answer sent');
    });

    socket.on('signal:answer', async (data: { callSessionId: string; sdp: RTCSessionDescriptionInit }) => {
      if (callTypeRef.current === 'chat') return;
      console.log('[CALL] Received answer');
      await pcReadyRef.current;
      const pc = pcRef.current;
      if (!pc) return;
      await pc.setRemoteDescription(new RTCSessionDescription(data.sdp));

      // Drain any ICE candidates that arrived before remote description was set
      for (const c of pendingIceCandidatesRef.current) {
        await pc.addIceCandidate(new RTCIceCandidate(c));
      }
      pendingIceCandidatesRef.current = [];
    });

    socket.on('signal:ice-candidate', async (data: { callSessionId: string; candidate: RTCIceCandidateInit }) => {
      if (callTypeRef.current === 'chat') return;
      await pcReadyRef.current;
      const pc = pcRef.current;
      if (!pc) return;

      // Buffer ICE candidates until remote description is set
      if (pc.remoteDescription) {
        await pc.addIceCandidate(new RTCIceCandidate(data.candidate));
      } else {
        pendingIceCandidatesRef.current.push(data.candidate);
      }
    });

    return () => {
      clearInterval(presenceInterval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      socket.disconnect();
      socketRef.current = null;
      setSocket(null);
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Al login/boot: se esiste una sessione viva (refresh/crash mid-call) proponi
  // il rientro. Solo da idle: se il flusso call è già in corso non serve.
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    void getActiveCall()
      .then((s) => {
        if (!cancelled && s && callStateRef.current === 'idle') setResumableSession(s);
      })
      .catch(() => {
        // Best-effort: senza risposta niente banner, la sessione resta recuperabile a mano.
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  // Mirror call identity/state into refs for the reconnect reconciler.
  useEffect(() => {
    callStateRef.current = callState;
  }, [callState]);
  useEffect(() => {
    callSessionIdRef.current = callInfo?.callSessionId ?? null;
  }, [callInfo]);
  useEffect(() => {
    incomingSessionIdRef.current = incomingCall?.callSessionId ?? null;
  }, [incomingCall]);
  useEffect(() => {
    pauseInfoRef.current = pauseInfo;
  }, [pauseInfo]);

  // Caller-side waiting audio: voice announcement + ringback while the
  // outgoing audio/video call is ringing (chat sessions stay silent). The
  // ref keeps the audio running across the initiating→ringing transition
  // (a bare start-on-every-run would restart the voice from the top); the
  // stop on every other state also covers cancel/timeout/error paths.
  const callerTonePlayingRef = useRef(false);
  useEffect(() => {
    const should = shouldPlayCallerTone(callState, activeCallType);
    if (should && !callerTonePlayingRef.current) {
      callerTonePlayingRef.current = true;
      startCallerTone();
    } else if (!should && callerTonePlayingRef.current) {
      callerTonePlayingRef.current = false;
      stopCallerTone();
    }
  }, [callState, activeCallType]);
  useEffect(() => () => stopCallerTone(), []); // unmount safety net

  // Schermo sempre acceso finché la call è viva: per il telefono una call
  // browser non è una telefonata, quindi il timeout schermo scatta e su molti
  // Android (Huawei/EMUI) lo spegnimento sospende la cattura del microfono —
  // l'altro lato smette di sentire (caso "la consulente non mi sente").
  // Vale anche per la chat a pagamento: lo schermo spento uccideva la sessione.
  useEffect(() => {
    if (callState === 'idle') {
      releaseScreenAwake();
    } else {
      keepScreenAwake(callInfo?.callSessionId);
    }
  }, [callState, callInfo?.callSessionId]);
  useEffect(() => () => releaseScreenAwake(), []); // unmount safety net

  // Elapsed timer during active call
  useEffect(() => {
    if (callState === 'active') {
      const start = Date.now();
      elapsedIntervalRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - start) / 1000));
      }, 1000);
    } else {
      if (elapsedIntervalRef.current) {
        clearInterval(elapsedIntervalRef.current);
        elapsedIntervalRef.current = null;
      }
      if (callState === 'idle') setElapsed(0);
    }

    return () => {
      if (elapsedIntervalRef.current) {
        clearInterval(elapsedIntervalRef.current);
      }
    };
  }, [callState]);

  async function setupWebRTC(iceServers: IceServerConfig[], callSessionId: string, socket: Socket, isCaller: boolean) {
    const pc = new RTCPeerConnection({
      iceServers: iceServers.map((s) => ({
        urls: s.urls,
        username: s.username,
        credential: s.credential,
      })),
    });
    pcRef.current = pc;

    // Diagnostica connessione (TODO #9): il ping del socket di segnalazione
    // parte subito, in parallelo a getUserMedia/ICE; il campione getStats()
    // viene inviato ~3s dopo il 'connected' (una volta per pc) o al 'failed'.
    // Best-effort: nulla qui deve poter rompere la chiamata.
    const diagPing = measureSocketPing(socket).catch(() => undefined);
    const diagDevice = detectDeviceModel();
    let diagConnectedScheduled = false;
    const sendDiagnostics = (phase: 'connected' | 'failed') => {
      collectNetDiagnostics(pc, phase, { pingMs: diagPing, deviceModel: diagDevice })
        .then((diagnostics) => {
          socket.emit('call:diagnostics', { callSessionId, diagnostics });
        })
        .catch(() => {});
    };

    // Get local media
    const isVideo = callTypeRef.current === 'video';
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true },
      video: isVideo ? { facingMode: 'user' } : false,
    });

    localStreamRef.current = stream;
    setLocalStream(stream);
    setIsVideoEnabled(isVideo);

    // Sorveglia la cattura del mic: se il sistema la sospende (schermo spento,
    // battery saver — l'altro lato smette di sentire) avvisa questa UI, il
    // peer e il Log Errori. Il mute manuale passa invece da toggleAudio.
    micWatchdogRef.current?.stop();
    micWatchdogRef.current = watchLocalMic(stream, callSessionId, (status) => {
      setMicSuspended(status !== 'ok');
      socket.emit('call:mic-status', {
        callSessionId,
        status:
          status !== 'ok' ? 'suspended' : isAudioMutedRef.current ? 'muted' : 'ok',
      });
    });

    stream.getTracks().forEach((track) => pc.addTrack(track, stream));

    // Remote stream
    const remoteMediaStream = new MediaStream();
    setRemoteStream(remoteMediaStream);

    pc.ontrack = (event) => {
      event.streams[0]?.getTracks().forEach((track) => {
        remoteMediaStream.addTrack(track);
      });
      setRemoteStream(new MediaStream(remoteMediaStream.getTracks()));
    };

    pc.onicecandidate = (event) => {
      if (event.candidate) {
        socket.emit('signal:ice-candidate', {
          callSessionId,
          candidate: event.candidate.toJSON(),
        });
      }
    };

    pc.oniceconnectionstatechange = () => {
      console.log('[CALL] ICE connection state:', pc.iceConnectionState);
    };

    pc.onconnectionstatechange = () => {
      console.log('[CALL] Connection state:', pc.connectionState);
      if (pc.connectionState === 'connected') {
        // Rientro (o rinegoziazione col peer) in una sessione già in pausa:
        // la media si è appena riconnessa ma lo stato resta 'paused', non
        // 'active' — altrimenti l'overlay di pausa sparirebbe da sotto i
        // piedi di chi sta ancora aspettando il resume.
        const pausedReason = pauseInfoRef.current?.reason;
        if (pausedReason) {
          setCallState('paused');
          // Pausa manuale: la traccia mic appena catturata nasce accesa —
          // va rimessa a tacere subito, stessa logica del call:paused live.
          if (pausedReason === 'manual') {
            stream.getAudioTracks().forEach((t) => { t.enabled = false; });
            setRemoteAudioMuted(true);
          }
        } else {
          setCallState('active');
        }
        // Tell the server the media actually connected — only connected calls
        // are billed at settlement.
        socket.emit('call:connected', { callSessionId });
        if (!diagConnectedScheduled) {
          diagConnectedScheduled = true;
          setTimeout(() => {
            // pc sostituita (rinegoziazione) o chiusa: il campione non ha senso.
            if (pcRef.current === pc && pc.connectionState !== 'closed') sendDiagnostics('connected');
          }, 3000);
        }
        startHeartbeat(callSessionId, socket);
        // Start recording the mixed audio streams
        if (stream && remoteMediaStream) {
          startRecording(stream, remoteMediaStream, callSessionId);
        }
      } else if (pc.connectionState === 'failed') {
        // ICE could not establish a path (typically mobile carrier NAT with no
        // reachable TURN relay). Surface a clear error and end the call so the
        // user isn't left on a silent, non-working session. The server settles
        // with no charge because call:connected was never sent.
        console.error('[CALL] connection failed — ending call');
        // Snapshot prima della cleanup (getStats parte in modo sincrono):
        // senza RTT, ma con tipi di candidato e info rete/dispositivo.
        sendDiagnostics('failed');
        setCallError('Impossibile stabilire la connessione. Verifica la rete e riprova.');
        socket.emit('call:end', { callSessionId });
        cleanup();
      }
      // 'disconnected' is transient (network blips) — let the heartbeat/server
      // grace window handle reconnect-or-end.
    };

    // Signal that PC is ready — unblocks signal handlers
    console.log('[CALL] PC ready, tracks added. isCaller:', isCaller);
    pcReadyResolverRef.current?.();

    // The caller creates the offer
    if (isCaller) {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      socket.emit('signal:offer', { callSessionId, sdp: offer });
      console.log('[CALL] Offer sent');
    }
  }

  function startHeartbeat(callSessionId: string, socket: Socket) {
    heartbeatIntervalRef.current = setInterval(() => {
      socket.emit('call:heartbeat', { callSessionId });
    }, 5000);
  }

  function startRecording(localStream: MediaStream, remoteStream: MediaStream, callSessionId: string) {
    activeRecordingRef.current = startCallRecording(localStream, remoteStream, callSessionId, {
      // Flush incrementale sul server: se la pagina muore prima di call:ended
      // (WebView in-app chiusa, schermo bloccato) l'audio fino all'ultimo
      // flush resta recuperabile per la trascrizione.
      uploadChunk: (chunk, offset, fileName) =>
        uploadRecordingChunk(callSessionId, chunk, offset, fileName),
    });
    if (activeRecordingRef.current) console.log('[CALL] Recording started');
  }

  function stopRecording(callSessionId: string) {
    const recording = activeRecordingRef.current;
    activeRecordingRef.current = null;
    if (!recording) return;

    void recording.stop().then(async (result) => {
      if (!result || !callSessionId) return;
      try {
        await uploadRecording(callSessionId, result.blob, result.fileName);
        console.log('[CALL] Recording uploaded');
      } catch (err) {
        console.error('[CALL] Failed to upload recording:', err);
        // Senza upload niente trascrizione: deve emergere nel Log Errori.
        reportClientErrorOnce(`recording-upload-${callSessionId}`, {
          code: 'CALL_RECORDING_UPLOAD_FAILED',
          message: 'Upload registrazione chiamata fallito: trascrizione non disponibile',
          context: {
            callSessionId,
            error: err instanceof Error ? `${err.name}: ${err.message}` : String(err),
          },
        });
      }
    });
  }

  // Smonta SOLO il layer media (PC, stream, heartbeat, audio remoto) senza
  // toccare lo stato della sessione: usato dalla rinegoziazione post-rejoin,
  // dove la call resta viva e va ricostruita da zero come al connect iniziale.
  function teardownPeerConnection() {
    detachRemoteAudio();
    micWatchdogRef.current?.stop();
    micWatchdogRef.current = null;
    setMicSuspended(false);
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((track) => track.stop());
      localStreamRef.current = null;
    }
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }
    pendingIceCandidatesRef.current = [];
    setLocalStream(null);
    setRemoteStream(null);
  }

  function cleanup() {
    // Release the shared remote-audio element (keeps its autoplay blessing)
    detachRemoteAudio();

    micWatchdogRef.current?.stop();
    micWatchdogRef.current = null;

    // Stop heartbeat
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }

    // Stop local media
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((track) => track.stop());
      localStreamRef.current = null;
    }

    // Close peer connection
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }

    callTypeRef.current = 'audio';
    pendingIceCandidatesRef.current = [];
    pcReadyRef.current = Promise.resolve();
    pcReadyResolverRef.current = null;
    setLocalStream(null);
    setRemoteStream(null);
    setCallState('idle');
    setCallInfo(null);
    setIncomingCall(null);
    setBillingTick(null);
    setClientRunway(null);
    setPromoEnding(null);
    setPauseInfo(null);
    pauseInfoRef.current = null;
    setIsAudioMuted(false);
    isAudioMutedRef.current = false;
    setMicSuspended(false);
    setRemoteMicStatus(null);
    setIsVideoEnabled(false);
    setElapsed(0);
    setActiveCallType(null);
    setPeerDisconnected(false);
  }

  // ---- Actions ----

  const initiateCall = useCallback(
    async (professionalId: string, callType: CallType, professionalName: string) => {
      if (callState !== 'idle') return;

      // A dead socket swallows the emit silently: the caller would sit on a
      // mute "initiating" screen with no session ever created server-side.
      if (!socketRef.current?.connected) {
        setCallError('Connessione non disponibile. Riprova tra qualche secondo.');
        return;
      }

      // Device permissions + audio unlock BEFORE any signaling, inside the
      // click gesture: a denial stops the call with a clear message, and a
      // browser that refuses even the silent unlock would leave the user in a
      // silent call — warn and block instead of letting the professional ring.
      if (callType !== 'chat') {
        const preflight = await runCallPreflight(callType);
        if (!preflight.ok && preflight.reason === 'permission') {
          setCallError(preflight.message);
          return;
        }
        if (!preflight.ok) {
          setCallError(AUDIO_BLOCKED_MESSAGE);
          reportClientErrorOnce(`autoplay-preflight-${Date.now()}`, {
            code: 'AUTOPLAY_BLOCKED_PREFLIGHT',
            message: 'Sblocco audio rifiutato al pre-flight: chiamata bloccata',
            context: { side: 'caller', callType },
          });
          return;
        }
      }

      callTypeRef.current = callType;
      setActiveCallType(callType);
      setCallState('initiating');
      setCallInfo({
        callSessionId: '', // will be set when server responds
        callType,
        remoteName: professionalName,
        costPerMinute: 0,
        professionalId,
      });
      setCallError(null);
      setCallEndInfo(null);

      const deviceModel = await detectDeviceModel();
      socketRef.current?.emit('call:initiate', { professionalId, callType, deviceModel });
    },
    [callState, setCallError]
  );

  const acceptIncomingCall = useCallback(async () => {
    if (!incomingCall) return;

    // Same pre-flight as the caller side: never enter a call without the
    // device permissions granted. The modal stays open so the professional
    // can fix the permission and tap Rispondi again.
    if (incomingCall.callType !== 'chat') {
      // Same audio-playback unlock as the caller side: a professional who
      // can't hear the customer is the same dead call.
      const preflight = await runCallPreflight(incomingCall.callType);
      if (!preflight.ok && preflight.reason === 'permission') {
        setCallError(preflight.message);
        return;
      }
      if (!preflight.ok) {
        setCallError(AUDIO_BLOCKED_MESSAGE);
        reportClientErrorOnce(`autoplay-preflight-${incomingCall.callSessionId}`, {
          code: 'AUTOPLAY_BLOCKED_PREFLIGHT',
          message: 'Sblocco audio rifiutato al pre-flight: risposta bloccata',
          context: { side: 'callee', callSessionId: incomingCall.callSessionId },
        });
        return;
      }
    }
    setCallError(null);

    setCallInfo({
      callSessionId: incomingCall.callSessionId,
      callType: incomingCall.callType,
      remoteName: incomingCall.callerName,
      remoteAvatar: incomingCall.callerAvatar,
      remoteDateOfBirth: incomingCall.callerDateOfBirth,
      costPerMinute: incomingCall.costPerMinute,
      // Callee (professional) side: badge tipologia + carry the caller's user id
      // so the chat session can resolve the existing conversation by userId.
      callerType: incomingCall.callerType,
      callerUserId: incomingCall.callerId,
      // Paid chat: conversation pre-created at initiate — lets the professional
      // open the thread and write first.
      conversationId: incomingCall.conversationId,
    });
    setCallState('connecting');

    // Same device-model capture as the caller side, for the admin call detail.
    const deviceModel = await detectDeviceModel();
    socketRef.current?.emit('call:accept', { callSessionId: incomingCall.callSessionId, deviceModel });
  }, [incomingCall, setCallError]);

  // Present a pending incoming call resolved from an SMS answer-link token.
  // Builds the SAME payload shape the `call:incoming` socket event sets, so the
  // existing IncomingCallModal renders with Rispondi/Rifiuta. callerId is needed
  // so a chat answer-link can resolve the conversation on the professional side.
  const presentIncomingFromToken = useCallback((info: AnswerCallInfo) => {
    callTypeRef.current = info.callType;
    setActiveCallType(info.callType);
    setIncomingCall({
      callSessionId: info.callSessionId,
      callerId: info.callerId,
      callerName: info.callerName,
      callerAvatar: info.callerAvatar,
      callerDateOfBirth: info.callerDateOfBirth,
      callType: info.callType,
      costPerMinute: info.costPerMinute,
      callerStatus: 'converted',
      callerType: info.callerType,
      conversationId: info.conversationId,
    });
    setCallState('incoming');
  }, []);

  const rejectIncomingCall = useCallback(() => {
    if (!incomingCall) return;

    socketRef.current?.emit('call:reject', { callSessionId: incomingCall.callSessionId });
    setIncomingCall(null);
    setActiveCallType(null);
    setCallState('idle');
  }, [incomingCall]);

  const cancelCall = useCallback(() => {
    if (!callInfo?.callSessionId) {
      cleanup();
      return;
    }

    socketRef.current?.emit('call:cancel', { callSessionId: callInfo.callSessionId });
    cleanup();
  }, [callInfo]);

  const endCall = useCallback(() => {
    if (!callInfo?.callSessionId) return;

    socketRef.current?.emit('call:end', { callSessionId: callInfo.callSessionId });
    // Don't cleanup yet - wait for call:ended from server
    setCallState('ending');
  }, [callInfo]);

  const toggleAudio = useCallback(() => {
    if (!localStreamRef.current) return;
    localStreamRef.current.getAudioTracks().forEach((t) => { t.enabled = !t.enabled; });
    const muted = !isAudioMutedRef.current;
    isAudioMutedRef.current = muted;
    setIsAudioMuted(muted);

    const callSessionId = callSessionIdRef.current;
    if (!callSessionId) return;
    // Avvisa l'altro lato (banner "non ti sente") e lascia traccia del primo
    // mute nel Log Errori: serve a distinguere il mute accidentale (guancia
    // sul bottone col telefono all'orecchio) dalla sospensione OS quando un
    // utente segnala "il consulente non mi sentiva".
    socketRef.current?.emit('call:mic-status', {
      callSessionId,
      status: muted ? 'muted' : 'ok',
    });
    if (muted) {
      reportClientErrorOnce(`call-mic-manual-mute-${callSessionId}`, {
        code: 'CALL_MIC_MUTED_MANUAL',
        message: 'Microfono disattivato dal bottone mute durante la chiamata',
        context: { callSessionId, visibility: document.visibilityState },
      });
    }
  }, []);

  const toggleVideo = useCallback(() => {
    if (!localStreamRef.current) return;
    localStreamRef.current.getVideoTracks().forEach((t) => { t.enabled = !t.enabled; });
    setIsVideoEnabled((prev) => !prev);
  }, []);

  const dismissCallEnd = useCallback(() => {
    setCallEndInfo(null);
  }, []);

  const dismissError = useCallback(() => {
    setCallError(null);
  }, [setCallError]);

  const dismissPhoneVerification = useCallback(() => {
    setPhoneVerificationRequired(false);
  }, []);

  const resumeCall = useCallback(() => {
    if (!callInfo?.callSessionId) return;
    socketRef.current?.emit('call:resume', { callSessionId: callInfo.callSessionId });
  }, [callInfo]);

  // Solo consulente, sessione ACTIVE (call o chat a pagamento): mette la
  // sessione in pausa manuale (fatturazione sospesa, max 5 minuti). Il
  // resume — SOLO la consulente — riusa resumeCall: stesso evento
  // 'call:resume' del percorso credito, nessuna azione server distinta.
  const pauseSession = useCallback(() => {
    if (!callInfo?.callSessionId) return;
    socketRef.current?.emit('call:pause', { callSessionId: callInfo.callSessionId });
  }, [callInfo]);

  const rejoinSession = useCallback(async () => {
    if (!resumableSession) return;
    const sock = socketRef.current;
    if (!sock?.connected) {
      setCallError('Connessione non disponibile. Riprova tra qualche secondo.');
      return;
    }

    const s = resumableSession;
    setResumableSession(null);
    callTypeRef.current = s.callType;
    setActiveCallType(s.callType);
    setCallInfo({
      callSessionId: s.callSessionId,
      callType: s.callType,
      remoteName: s.peerName,
      remoteAvatar: s.peerAvatar,
      costPerMinute: s.costPerMinute,
      conversationId: s.conversationId,
      callerUserId: s.role === 'callee' ? s.peerUserId : undefined,
      callerType: s.role === 'callee' ? s.callerType : undefined,
    });
    setCallError(null);

    if (s.callType === 'chat') {
      // Nessun media: si riprende subito lo stato attivo e l'heartbeat; il
      // server azzera il proprio timer alla ricezione di call:rejoin.
      setCallState('active');
      startHeartbeat(s.callSessionId, sock);
      sock.emit('call:rejoin', { callSessionId: s.callSessionId });
      return;
    }

    // Stesso pre-flight del connect: permessi device e sblocco audio nel gesto.
    const preflight = await runCallPreflight(s.callType);
    if (!preflight.ok) {
      setCallError(preflight.reason === 'permission' ? preflight.message : AUDIO_BLOCKED_MESSAGE);
      cleanup();
      return;
    }
    setCallState('connecting');
    sock.emit('call:rejoin', { callSessionId: s.callSessionId });
  }, [resumableSession, setCallError]);

  const declineResume = useCallback(() => {
    if (!resumableSession) return;
    socketRef.current?.emit('call:end', { callSessionId: resumableSession.callSessionId });
    setResumableSession(null);
  }, [resumableSession]);

  return (
    <CallContext.Provider
      value={{
        callState,
        callInfo,
        activeCallType,
        localStream,
        remoteStream,
        isAudioMuted,
        micSuspended,
        remoteMicStatus,
        isVideoEnabled,
        elapsed,
        billingTick,
        clientRunway,
        promoEnding,
        callEndInfo,
        incomingCall,
        callError,
        callErrorSeverity,
        phoneVerificationRequired,
        pauseInfo,
        pauseSession,
        socket,
        initiateCall,
        acceptIncomingCall,
        rejectIncomingCall,
        presentIncomingFromToken,
        cancelCall,
        endCall,
        toggleAudio,
        toggleVideo,
        dismissCallEnd,
        dismissError,
        dismissPhoneVerification,
        resumeCall,
        resumableSession,
        peerDisconnected,
        rejoinSession,
        declineResume,
      }}
    >
      {children}
    </CallContext.Provider>
  );
}
